import React from 'react';
import { MapPin, Calendar, ArrowRight, Building2 } from 'lucide-react';
import { StatusBadge, PriorityBadge } from './Badges';

interface MobileDemandCardProps {
  demand: any;
  onView: (id: string) => void;
  onEdit?: (id: string) => void;
  canEdit?: boolean;
  compact?: boolean;
}

const SOURCE_ICON: Record<string, string> = {
  WHATSAPP: '💬',
  EMAIL:    '✉️',
  PDF:      '📄',
  IMAGE:    '🖼️',
  MANUAL:   '✏️',
  PRESENCIAL: '🏛️',
};

const CABINET_LABEL: Record<string, { label: string; color: string }> = {
  STATE:   { label: 'Estadual', color: 'text-blue-600 bg-blue-50 dark:bg-blue-950/30 dark:text-blue-300' },
  FEDERAL: { label: 'Federal',  color: 'text-red-600  bg-red-50  dark:bg-red-950/30  dark:text-red-300'  },
  BOTH:    { label: 'Ambos',    color: 'text-amber-600 bg-amber-50 dark:bg-amber-950/30 dark:text-amber-300' },
};

export const MobileDemandCard: React.FC<MobileDemandCardProps> = ({
  demand,
  onView,
  onEdit,
  canEdit = false,
  compact = false,
}) => {
  const cabinet = CABINET_LABEL[demand.cabinet] ?? { label: demand.cabinet, color: 'text-slate-500 bg-slate-100' };
  const sourceEmoji = SOURCE_ICON[demand.source] ?? '📋';
  const dateStr = demand.createdAt
    ? new Date(demand.createdAt).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })
    : '';

  return (
    <article
      className="mobile-card bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-4 cursor-pointer transition-all shadow-sm"
      onClick={() => onView(demand.id)}
    >
      {/* Top row: protocol + priority + source */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-mono font-bold text-slate-400 dark:text-slate-500">
            {demand.protocol}
          </span>
          <span className="text-sm">{sourceEmoji}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <PriorityBadge priority={demand.priority} size="xs" />
          <StatusBadge status={demand.status} size="xs" />
        </div>
      </div>

      {/* Title */}
      <h3 className="text-sm font-bold text-slate-800 dark:text-slate-100 leading-snug line-clamp-2 mb-2">
        {demand.title}
      </h3>

      {!compact && (
        <>
          {/* Solicitante */}
          {demand.contact?.nameEntity && (
            <div className="flex items-center gap-1.5 mb-1.5">
              <Building2 className="w-3.5 h-3.5 text-slate-400 shrink-0" />
              <span className="text-xs text-slate-500 truncate">{demand.contact.nameEntity}</span>
            </div>
          )}

          {/* Cidade + Data */}
          <div className="flex items-center justify-between mt-2">
            <div className="flex items-center gap-1.5">
              <MapPin className="w-3.5 h-3.5 text-slate-400 shrink-0" />
              <span className="text-xs text-slate-500">
                {demand.city}{demand.uf ? ` — ${demand.uf}` : ''}
              </span>
            </div>
            <div className="flex items-center gap-3">
              {/* Cabinet badge */}
              <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-md ${cabinet.color}`}>
                {cabinet.label}
              </span>
              <div className="flex items-center gap-1 text-slate-400">
                <Calendar className="w-3 h-3" />
                <span className="text-[10px]">{dateStr}</span>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Action row */}
      {canEdit && !compact && (
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100 dark:border-slate-800">
          <button
            onClick={(e) => { e.stopPropagation(); onEdit?.(demand.id); }}
            className="text-[10px] font-semibold text-blue-600 dark:text-blue-400 hover:underline"
          >
            Editar
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onView(demand.id); }}
            className="flex items-center gap-1 text-[10px] font-semibold text-slate-500 dark:text-slate-400"
          >
            Ver detalhes <ArrowRight className="w-3 h-3" />
          </button>
        </div>
      )}
    </article>
  );
};
